import config from '@/config'
import { fetchWithThrow } from '@/lib/queries'
import { sessionTokenAtom } from '@/stores/session'
import { AUTHORIZATION_HEADER_PREFIX } from '@repo/constants'
import type { RecordingUpload } from '@vol7/test-lab-recorder'
import type { RecorderLabels } from '@vol7/test-lab-recorder/react'
import { useAtomValue } from 'jotai'
import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// Split out so the recorder and rrweb only download where the flag is on.
const RecorderWidget = lazy(() => import('@vol7/test-lab-recorder/react'))

const uploadedSchema = z.object({ id: z.string() })

// The lab page of a sent recording, where the recorder runs; none elsewhere.
const recordingLink = (body: unknown) => {
  const { id } = uploadedSchema.parse(body)
  return {
    url: config.testLabURL
      ? `${config.testLabURL}/recordings/${encodeURIComponent(id)}`
      : undefined,
  }
}

/** The client's "report a problem / record a scenario" button, on the environments that run a recette. */
export function ClientRecorder() {
  const { t } = useTranslation('recorder')
  const sessionToken = useAtomValue(sessionTokenAtom)

  if (!config.featureFlags['client-recorder'] || !sessionToken) return null

  const uploadRecording = async ({ draft, events }: RecordingUpload) => {
    const body = new FormData()
    body.append('draft', JSON.stringify(draft))
    body.append('events', new Blob([JSON.stringify(events)], { type: 'application/json' }))
    const res = await fetchWithThrow(`${config.backendURL}/recordings`, {
      method: 'POST',
      headers: { Authorization: `${AUTHORIZATION_HEADER_PREFIX}${sessionToken}` },
      body,
    })
    return recordingLink(await res.json())
  }

  const labels: RecorderLabels = {
    trigger: t('trigger'),
    reportProblem: t('reportProblem'),
    reportProblemHint: t('reportProblemHint'),
    recordScenario: t('recordScenario'),
    recordScenarioHint: t('recordScenarioHint'),
    recording: t('recording'),
    addCheck: t('addCheck'),
    pickElementHint: t('pickElementHint'),
    checkNoteLabel: t('checkNoteLabel'),
    addCheckConfirm: t('addCheckConfirm'),
    finish: t('finish'),
    cancel: t('cancel'),
    reportTitle: t('reportTitle'),
    scenarioTitle: t('scenarioTitle'),
    titleLabel: t('titleLabel'),
    commentLabel: t('commentLabel'),
    send: t('send'),
    sending: t('sending'),
    sent: t('sent'),
    sendFailed: t('sendFailed'),
    privacyNote: t('privacyNote'),
    previewLabel: t('previewLabel'),
    play: t('play'),
    pause: t('pause'),
    openRecording: t('openRecording'),
    close: t('close'),
    checksTitle: t('checksTitle'),
    checksHint: t('checksHint'),
    showCheck: t('showCheck'),
    removeCheck: t('removeCheck'),
    restoreCheck: t('restoreCheck'),
    next: t('next'),
    seek: t('seek'),
    optional: t('optional'),
    addComment: t('addComment'),
    typeLabel: t('typeLabel'),
    typeBug: t('typeBug'),
    typeImprovement: t('typeImprovement'),
    typeQuestion: t('typeQuestion'),
    priorityLabel: t('priorityLabel'),
    speed: t('speed'),
    trim: t('trim'),
    trimStart: t('trimStart'),
    trimEnd: t('trimEnd'),
  }

  return (
    <Suspense fallback={null}>
      <RecorderWidget
        app={{ name: 'driver-portal' }}
        captureRequestsTo={[config.backendURL]}
        labels={labels}
        upload={uploadRecording}
      />
    </Suspense>
  )
}
